import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, from, map, of, switchMap } from 'rxjs';
import {
  ButtonComponent,
  DIALOG_REF,
  DialogRef,
  IconComponent,
  LanguageSelectorComponent,
  TooltipDirective,
} from 'continity-shared-ui';
import { AuthService, ShellThemeService } from 'continity-shared-shell';

import { LegalSectionsComponent } from './legal-sections.component';
import { PhoenixPrivacyConsentService } from './phoenix-privacy-consent.service';
import { PRIVACY_CONSENT_VERSION } from './privacy-consent.version';

/**
 * Blocking GDPR/terms consent gate of the Phoenix portal — the equivalent of the main
 * portal's `PrivacyConsentDialogComponent`, opened by {@link ShellLayoutComponent} whenever
 * the tenant has not yet accepted the CURRENT consent version (see the architecture note on
 * {@link PhoenixPrivacyConsentService} for why this is a local mirror sharing the backend
 * contract + i18n bundles instead of a shared component).
 *
 * Same acceptance mechanics as the main portal: the terms must be actively checked, the
 * privacy policy read (scroll-to-bottom auto-ticks its checkbox; manual ticking stays
 * possible), and declining hard-gates via logout. One Phoenix-specific addition: the backend
 * only lets BCMEditor/CompanyAdmin write the tenant-wide consent, and a Phoenix-role user
 * (PhoenixReader/Operator/Admin) carries NEITHER — instead of letting them collect a 403,
 * the accept button is disabled with an explanatory hint (`phoenix.consent.adminOnlyHint`).
 *
 * Deviation from the main portal's dialog: no support-request button (the support dialog
 * stack is main-portal-only infrastructure; Phoenix support goes through the Continity
 * Phoenix Team channels instead).
 */
@Component({
  selector: 'phoenix-consent-gate-dialog',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonComponent,
    IconComponent,
    LanguageSelectorComponent,
    LegalSectionsComponent,
    TooltipDirective,
  ],
  template: `
    <div class="h-full flex flex-col bg-white dark:bg-gray-900" data-cy="phoenix-consent-dialog">
      <!-- Header -->
      <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 sticky top-0 z-10">
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-4 min-w-0">
            <div class="p-3 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <app-icon name="shield" [size]="28" color="rgb(249 115 22)"></app-icon>
            </div>
            <div class="min-w-0 flex flex-col">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                {{ 'legalConsent.title' | translate }}
              </h2>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5 leading-snug">
                {{ 'legalConsent.subtitle' | translate }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3 shrink-0">
            <app-language-selector></app-language-selector>
            <!-- Tooltip + aria-label like the shell's own theme toggle (the shared-ui language
                 selector next door uses the same appTooltip idiom, key-driven so it re-renders on
                 language change); aria-label mirrors landing-page.component.ts. -->
            <button
              type="button"
              class="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition flex items-center justify-center"
              (click)="theme.toggle()"
              appTooltip
              appTooltipKey="phoenix.shell.themeToggle"
              position="bottom"
              [attr.aria-label]="'phoenix.shell.themeToggle' | translate"
              data-cy="phoenix-consent-theme-toggle">
              <app-icon [name]="(theme.isDarkMode$ | async) ? 'sun' : 'moon'"></app-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-6 py-6">
        <div class="space-y-4">
          <!-- ================= Terms (AGB) ================= -->
          <section class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
            <button
              type="button"
              class="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
              (click)="termsExpanded = !termsExpanded"
              data-cy="phoenix-consent-terms-toggle">
              <div class="text-left">
                <div class="font-semibold text-gray-900 dark:text-gray-100">
                  {{ 'legalConsent.agb.title' | translate }}
                </div>
                <div class="text-xs text-gray-600 dark:text-gray-400">
                  {{ 'legalConsent.lastUpdated' | translate: { date: ('legalConsent.agb.stand' | translate) } }}
                </div>
              </div>
              <app-icon [name]="termsExpanded ? 'chevron-up' : 'chevron-down'"></app-icon>
            </button>

            @if (termsExpanded) {
              <div class="px-4 pb-4">
                <div
                  class="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 p-4 overflow-y-auto"
                  style="max-height: 45vh;"
                  data-cy="phoenix-consent-terms-scrollbox">
                  <div class="text-gray-800 dark:text-gray-200">
                    <phoenix-legal-sections rootKey="legalConsent.agb.sections" variant="dialog">
                    </phoenix-legal-sections>
                  </div>
                </div>
                <label class="mt-4 flex items-start gap-3 cursor-pointer" data-cy="phoenix-consent-terms-label">
                  <input
                    type="checkbox"
                    [(ngModel)]="termsAccepted"
                    (ngModelChange)="errorMessage = null"
                    class="mt-1 h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-2 focus:ring-orange-500"
                    data-cy="phoenix-consent-terms-checkbox" />
                  <span class="text-sm text-gray-800 dark:text-gray-200">
                    {{ 'legalConsent.requiredCheckboxes.terms' | translate }}
                  </span>
                </label>
              </div>
            }
          </section>

          <!-- ================= Privacy policy ================= -->
          <section class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
            <button
              type="button"
              class="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
              (click)="privacyExpanded = !privacyExpanded"
              data-cy="phoenix-consent-privacy-toggle">
              <div class="text-left">
                <div class="font-semibold text-gray-900 dark:text-gray-100">
                  {{ 'privacyPolicy.title' | translate }}
                </div>
                <div class="text-xs text-gray-600 dark:text-gray-400">
                  {{ 'privacyPolicy.lastUpdated' | translate }}
                </div>
                <p
                  class="text-xs mt-1"
                  [class.text-gray-500]="!hasReadPrivacy"
                  [class.dark:text-gray-400]="!hasReadPrivacy"
                  [class.text-green-600]="hasReadPrivacy"
                  [class.dark:text-green-400]="hasReadPrivacy">
                  {{ (hasReadPrivacy ? 'privacyPolicy.readDone' : 'privacyPolicy.readHint') | translate }}
                </p>
              </div>
              <app-icon [name]="privacyExpanded ? 'chevron-up' : 'chevron-down'"></app-icon>
            </button>

            @if (privacyExpanded) {
              <div class="px-4 pb-4">
                <div
                  class="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 p-4 overflow-y-auto"
                  style="max-height: 45vh;"
                  (scroll)="onPrivacyScroll($event)"
                  data-cy="phoenix-consent-privacy-scrollbox">
                  <div class="text-gray-800 dark:text-gray-200">
                    <phoenix-legal-sections rootKey="privacyPolicy.sections" variant="dialog">
                    </phoenix-legal-sections>
                  </div>
                </div>
                <label class="mt-4 flex items-start gap-3 cursor-pointer" data-cy="phoenix-consent-privacy-label">
                  <input
                    type="checkbox"
                    [(ngModel)]="privacyAcknowledged"
                    (ngModelChange)="errorMessage = null"
                    class="mt-1 h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-2 focus:ring-orange-500"
                    data-cy="phoenix-consent-privacy-checkbox" />
                  <span class="text-sm text-gray-800 dark:text-gray-200">
                    {{ 'legalConsent.requiredCheckboxes.privacyAck' | translate }}
                  </span>
                </label>
              </div>
            }
          </section>

          <!-- Phoenix-only: the tenant-wide consent needs an admin/editor role to be written -->
          @if (!canAcceptRole()) {
            <div
              class="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-700"
              data-cy="phoenix-consent-admin-hint">
              <app-icon name="info-circle" [size]="16" class="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"></app-icon>
              <p class="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                {{ 'phoenix.consent.adminOnlyHint' | translate }}
              </p>
            </div>
          }

          @if (errorMessage) {
            <div class="text-sm text-red-600 dark:text-red-400" data-cy="phoenix-consent-error">
              {{ errorMessage | translate }}
            </div>
          }
        </div>
      </div>

      <!-- Footer -->
      <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-900">
        <div class="flex items-center justify-end gap-3">
          <app-button
            type="button"
            variant="secondary"
            size="md"
            (click)="onReject()"
            data-cy="phoenix-consent-reject">
            {{ 'legalConsent.reject' | translate }}
          </app-button>
          <app-button
            type="button"
            variant="primary"
            size="md"
            [disabled]="!canAccept"
            (click)="onAccept()"
            data-cy="phoenix-consent-accept">
            {{ 'legalConsent.accept' | translate }}
          </app-button>
        </div>
      </div>
    </div>
  `,
})
export class ConsentGateDialogComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly consentService = inject(PhoenixPrivacyConsentService);
  private readonly dialogRef = inject<DialogRef<ConsentGateDialogComponent, boolean>>(DIALOG_REF);
  readonly theme = inject(ShellThemeService);

  termsExpanded = true;
  privacyExpanded = true;

  termsAccepted = false;
  /** True once the privacy scrollbox has been scrolled to the bottom. */
  hasReadPrivacy = false;
  /** Auto-ticked by the scroll gate, manually tickable as well (main-portal behaviour). */
  privacyAcknowledged = false;

  errorMessage: string | null = null;
  private saving = false;

  /**
   * Whether THIS user may write the tenant-wide consent — the backend restricts the accept to
   * BCMEditor/CompanyAdmin (canonical role-title strings from `RoleTitles.cs`). Defaults to
   * true until roles resolve so the button does not flash disabled for an admin.
   */
  readonly canAcceptRole = toSignal(
    from(this.auth.waitForAuthInit()).pipe(
      switchMap(() => this.auth.getUserRoles()),
      map((roles) => roles.includes('CompanyAdmin') || roles.includes('BCMEditor')),
      catchError((err) => {
        // Fail-open on the ROLE READ only: the backend still enforces the role on accept, so
        // the worst case is the main-portal behaviour (a 403 surfaces as an error message).
        console.warn('[ConsentGate] Failed to resolve user roles for the accept gate:', err);
        return of(true);
      })
    ),
    { initialValue: true }
  );

  ngOnInit(): void {
    // Belt and braces: the shell opens this dialog only for authenticated users, but a stale
    // session that expired between the status read and the dialog would render a dead gate.
    if (!this.auth.isAuthenticated) {
      console.warn('[ConsentGate] Opened without an authenticated session — closing.');
      this.dialogRef.close(false);
    }
  }

  get canAccept(): boolean {
    return this.termsAccepted && this.privacyAcknowledged && this.canAcceptRole() && !this.saving;
  }

  /** Scroll gate on the privacy scrollbox only (same 8px threshold as the main portal). */
  onPrivacyScroll(event: Event): void {
    if (this.hasReadPrivacy) return;

    const el = event.target as HTMLElement;
    const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (reachedBottom) {
      this.hasReadPrivacy = true;
      this.privacyAcknowledged = true;
      this.errorMessage = null;
    }
  }

  onAccept(): void {
    if (!this.termsAccepted || !this.privacyAcknowledged) {
      this.errorMessage = 'legalConsent.acceptMissing';
      return;
    }
    if (!this.canAcceptRole() || this.saving) return;

    this.saving = true;
    this.errorMessage = null;

    this.consentService.accept(PRIVACY_CONSENT_VERSION).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err: { status?: number }) => {
        this.saving = false;
        console.warn(
          `[ConsentGate] Accepting consent version ${PRIVACY_CONSENT_VERSION} failed ` +
            `(HTTP ${err?.status ?? 'unknown'}):`,
          err
        );
        // 403 = the role gate (INSUFFICIENT_ROLE) — explain it instead of a generic failure.
        this.errorMessage =
          err?.status === 403 ? 'phoenix.consent.adminOnlyHint' : 'legalConsent.acceptError';
      },
    });
  }

  /** Hard gate, main-portal semantics: no consent ⇒ no portal. */
  onReject(): void {
    this.auth.logout();
  }
}
